import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy, ChevronRight, Target, CheckCircle2 } from "lucide-react";

const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

function RewriteResult({ rw, isAdmin, onApplied }) {
  const [busy, setBusy] = useState(false);
  const toggleApplied = async () => {
    setBusy(true);
    try {
      await api.post("/admin/traffic/opportunities/applied", { path: rw.path, applied: !rw.applied_at });
      toast.success(rw.applied_at ? "Unmarked" : "Marked as applied — CTR tracking started");
      onApplied();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <div className="mt-3 rounded-lg bg-cream border border-pollen/60 p-4 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-clay mb-1">Current title ({(rw.current_title || "").length}c)</div>
          <div className="text-xs text-inkmute line-through decoration-red-300">{rw.current_title || "(empty)"}</div>
          <div className="text-[10px] uppercase tracking-widest text-clay mt-3 mb-1 flex items-center gap-2">
            Proposed title ({(rw.proposed_title || "").length}c)
            <button onClick={() => copy(rw.proposed_title, "Title")} className="text-forest"><Copy className="w-3 h-3" /></button>
          </div>
          <div className="text-sm text-ink font-medium">{rw.proposed_title}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-clay mb-1">Current meta ({(rw.current_meta || "").length}c)</div>
          <div className="text-xs text-inkmute line-through decoration-red-300">{rw.current_meta || "(empty)"}</div>
          <div className="text-[10px] uppercase tracking-widest text-clay mt-3 mb-1 flex items-center gap-2">
            Proposed meta ({(rw.proposed_meta || "").length}c)
            <button onClick={() => copy(rw.proposed_meta, "Meta description")} className="text-forest"><Copy className="w-3 h-3" /></button>
          </div>
          <div className="text-sm text-ink">{rw.proposed_meta}</div>
        </div>
      </div>
      {rw.rationale && <div className="text-xs text-inkmute italic">{rw.rationale}</div>}
      {(rw.queries || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {rw.queries.slice(0, 8).map((q) => (
            <span key={q.query} className="text-[10px] px-2 py-0.5 rounded-full bg-beige border border-forest/15 text-ink" title={`${q.impressions} impressions · position ${q.position.toFixed(1)}`}>
              {q.query}
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[10px] text-inkmute">Generated {new Date(rw.generated_at).toLocaleString()} · {rw.model}</div>
        {isAdmin && (
          <button data-testid={`opp-applied-${rw.path}`} onClick={toggleApplied} disabled={busy}
            className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full disabled:opacity-50 ${rw.applied_at ? "bg-forest text-cream" : "border border-forest/25 text-forest hover:bg-forest hover:text-cream transition-colors"}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            {rw.applied_at ? `Applied ${new Date(rw.applied_at).toLocaleDateString()} · undo` : "Mark as applied on site"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function ClickOpportunities({ days, isAdmin, onAppliedChange }) {
  const [opps, setOpps] = useState(null);
  const [job, setJob] = useState(null);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = () => api.get(`/admin/traffic/opportunities?days=${days}`).then((r) => { setOpps(r.data.opportunities); setJob(r.data.job); }).catch(() => {});
  useEffect(() => { load(); }, [days]);
  useEffect(() => {
    if (!job?.running) return;
    const t = setInterval(load, 7000);
    return () => clearInterval(t);
  }, [job?.running]);

  const writeAll = async () => {
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/traffic/opportunities/rewrite-all", { days });
      if (r.data.queued === 0) toast.info(r.data.message);
      else toast.success(`Writing ${r.data.queued} rewrites in the background…`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start"); }
    setBulkBusy(false);
  };

  const generate = async (path) => {
    setBusy((b) => ({ ...b, [path]: true }));
    try {
      const r = await api.post("/admin/traffic/opportunities/rewrite", { path });
      setOpps((prev) => prev.map((o) => (o.path === path ? { ...o, rewrite: r.data } : o)));
      setOpen((v) => ({ ...v, [path]: true }));
      toast.success("Rewrite ready");
    } catch (e) { toast.error(e.response?.data?.detail || "Rewrite failed"); }
    setBusy((b) => ({ ...b, [path]: false }));
  };

  if (!opps || opps.length === 0) return null;

  return (
    <div className="rounded-xl border border-pollen bg-beige p-5 mb-6" data-testid="click-opportunities">
      <div className="text-[10px] uppercase tracking-widest text-clay mb-1 flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-1.5"><Target className="w-3.5 h-3.5" /> Click opportunities ({opps.length})</span>
        {isAdmin && (job?.running ? (
          <span className="inline-flex items-center gap-1.5 normal-case tracking-normal text-xs bg-pollen text-ink px-3 py-1 rounded-full"><Loader2 className="w-3 h-3 animate-spin" /> writing {job.done}/{job.total}…</span>
        ) : opps.some((o) => !o.rewrite) ? (
          <button data-testid="opp-rewrite-all" onClick={writeAll} disabled={bulkBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-forest text-cream hover:bg-forestDeep disabled:opacity-50">
            <Sparkles className="w-3 h-3" /> Write all rewrites ({opps.filter((o) => !o.rewrite).length})
          </button>
        ) : null)}
      </div>
      <p className="text-xs text-inkmute mb-4">These pages appear in Google but win almost no clicks. AI rewrites the title &amp; meta description around the exact queries each page already shows up for — paste them into the site and watch CTR.</p>
      <div className="space-y-2">
        {opps.map((o) => (
          <div key={o.path} className="rounded-lg bg-cream border border-forest/10 p-3">
            <div className="flex flex-wrap items-center gap-3">
              <ChevronRight className={`w-4 h-4 text-inkmute shrink-0 transition-transform ${open[o.path] ? "rotate-90" : ""} ${o.rewrite ? "cursor-pointer" : "opacity-30"}`}
                onClick={() => o.rewrite && setOpen((v) => ({ ...v, [o.path]: !v[o.path] }))} />
              <div className="min-w-0 flex-1">
                <div className="text-xs text-ink truncate" title={o.path}>{o.path}</div>
                <div className="text-[10px] text-inkmute">{o.impressions} impressions · {o.clicks} clicks{o.position ? ` · position ${o.position.toFixed(1)}` : ""} · {o.group}</div>
              </div>
              {isAdmin && (
                <button data-testid={`opp-rewrite-${o.path}`} onClick={() => generate(o.path)} disabled={busy[o.path]}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-forest text-cream hover:bg-forestDeep disabled:opacity-50 shrink-0">
                  {busy[o.path] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {busy[o.path] ? "Writing…" : o.rewrite ? "Regenerate" : "Write AI rewrite"}
                </button>
              )}
            </div>
            {o.rewrite && open[o.path] && <RewriteResult rw={o.rewrite} isAdmin={isAdmin} onApplied={() => { load(); onAppliedChange && onAppliedChange(); }} />}
          </div>
        ))}
      </div>
    </div>
  );
}
